import { all, call, put, takeEvery, select } from 'redux-saga/effects'
import { seasonActions } from '@redux/store/index'
import { seasonProductApi, ResType } from '@api'
import {
	SeasonProductType,
	SeasonProductBatchUpdateType,
	SeasonProductPhaseType,
	SeasonProductPhaseUpdateType,
} from '@schemas'
import type { PayloadAction } from '@reduxjs/toolkit'

// API call to fetch season product
const fetchSeasonProduct = async (seasonId: string): Promise<SeasonProductType[]> => {
	const response = await seasonProductApi.getSeasonProduct({ query: { seasonId } })
	return (response?.data as SeasonProductType[]) || []
}

// API call to batch update season product
const batchUpdateSeasonProduct = async (
	productList: SeasonProductBatchUpdateType[],
): Promise<ResType | undefined> => {
	const response = await seasonProductApi.batchUpdateSeasonProduct({
		body: { productList },
	})
	return response
}

// separate function to handle the fetching logic
function* fetchSeasonProductSaga(action: PayloadAction<string>) {
	try {
		const seasonProducts: SeasonProductType[] = yield call(
			fetchSeasonProduct,
			action.payload,
		)
		yield put(seasonActions.SET_SEASON_PRODUCT_LIST(seasonProducts))
	} catch (error) {
		console.error('Error fetching Season Product:', error)
	}
}

// Saga to handle the batch update action
const batchUpdateSeasonProductSaga = function* (
	action: PayloadAction<SeasonProductBatchUpdateType[]>,
): Generator {
	try {
		yield call(batchUpdateSeasonProduct, action.payload)
		const selectedSeasonId = yield select(state => state.season.selectedSeason?.id)
		if (selectedSeasonId) {
			yield put(seasonActions.FETCH_SEASON_PRODUCT(selectedSeasonId as string))
		}
	} catch (error) {
		console.error('Error batch updating Season Product:', error)
	}
}

// Watcher saga
export function* watchFetchSeasonProduct() {
	yield takeEvery(seasonActions.FETCH_SEASON_PRODUCT.type, fetchSeasonProductSaga)
}

export function* watchBatchUpdateSeasonProduct() {
	yield takeEvery(
		seasonActions.BATCH_UPDATE_SEASON_PRODUCT.type,
		batchUpdateSeasonProductSaga,
	)
}

// 판매·이행 단계 (CATALOG_PLAN §7.4)
const fetchPhase = async (seasonProductId: string): Promise<SeasonProductPhaseType[]> => {
	const response = await seasonProductApi.getSeasonProductPhase({
		params: { seasonProductId },
	})
	return (response?.data as SeasonProductPhaseType[]) || []
}

const batchUpdatePhase = async (
	seasonProductId: string,
	phaseList: SeasonProductPhaseUpdateType[],
): Promise<ResType | undefined> => {
	return await seasonProductApi.batchUpdateSeasonProductPhase({
		params: { seasonProductId },
		body: { phaseList },
	})
}

function* fetchSeasonProductPhaseSaga(action: PayloadAction<string>) {
	try {
		const phases: SeasonProductPhaseType[] = yield call(fetchPhase, action.payload)
		yield put(seasonActions.SET_SEASON_PRODUCT_PHASE_LIST(phases))
	} catch (error) {
		console.error('Error fetching Season Product Phase:', error)
		yield put(seasonActions.SET_SEASON_PRODUCT_PHASE_LIST([]))
	}
}

function* batchUpdateSeasonProductPhaseSaga(
	action: PayloadAction<{
		seasonProductId: string
		phaseList: SeasonProductPhaseUpdateType[]
	}>,
): Generator {
	try {
		yield call(batchUpdatePhase, action.payload.seasonProductId, action.payload.phaseList)
		yield put(seasonActions.BATCH_UPDATE_SEASON_PRODUCT_PHASE_SUCCESS())
		// 저장한 값을 서버에서 다시 읽는다 — 단계가 바뀌면 목록의 가격·상태도 같이 바뀐다
		yield put(seasonActions.FETCH_SEASON_PRODUCT_PHASE(action.payload.seasonProductId))
		const selectedSeasonId = yield select(state => state.season.selectedSeason?.id)
		if (selectedSeasonId) {
			yield put(seasonActions.FETCH_SEASON_PRODUCT(selectedSeasonId as string))
		}
	} catch (error: any) {
		console.error('Error batch updating Season Product Phase:', error)
		yield put(
			seasonActions.BATCH_UPDATE_SEASON_PRODUCT_PHASE_FAILURE(
				error?.message ?? 'PHASE_UPDATE_FAILED',
			),
		)
	}
}

export function* watchFetchSeasonProductPhase() {
	yield takeEvery(
		seasonActions.FETCH_SEASON_PRODUCT_PHASE.type,
		fetchSeasonProductPhaseSaga,
	)
}

export function* watchBatchUpdateSeasonProductPhase() {
	yield takeEvery(
		seasonActions.BATCH_UPDATE_SEASON_PRODUCT_PHASE.type,
		batchUpdateSeasonProductPhaseSaga,
	)
}

export default function* seasonProductSaga() {
	yield all([
		watchFetchSeasonProduct(),
		watchBatchUpdateSeasonProduct(),
		watchFetchSeasonProductPhase(),
		watchBatchUpdateSeasonProductPhase(),
	])
}
